import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import CommentsTable from "./components/CommentsTable";
import BookingAndDeceasedDetails from "./components/BookingDeceasedDetails";
import TabButtons from "./components/TabButtons";
import { MemoryTableGallery } from "./components/MemoryTableGallery";
import { MemoryTableUpload } from "./components/MemoryTableUpload";
import { MemoryBookDelivery } from "./components/MemoryBookDelivery";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";
import { showErrorToast } from "../../../../components/common/ShowToast";

type activeTabType =
  | "tribute-comments"
  | "youtube-comments"
  | "media"
  | "upload"
  | "delivery";

type MemoryTableView2Props = {
  streamId?: string;
  hideHeaderDetails?: boolean;
};

const MemoryTableView2: React.FC<MemoryTableView2Props> = ({
  streamId,
  hideHeaderDetails = false,
}) => {
  const params = useParams<{ id: string }>();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const resolvedStreamId = useMemo(() => {
    return streamId ?? params.id ?? "";
  }, [streamId, params.id]);

  const [activeTab, setActiveTab] = useState<activeTabType>("tribute-comments");
  const [commentsWaitingApproval, setCommentsWaitingApproval] =
    useState<number>(0);

  const fetchApprovedComments = useCallback(async () => {
    if (!resolvedStreamId) return;

    try {
      const response = await apiService.get<{ count: number }>(
        `/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=generic&is_approved=false`,
      );
      setCommentsWaitingApproval(response.data.count);
    } catch (error) {
      console.error("Failed to fetch approved comments:", error);
      showErrorToast("Failed to fetch approved comments");
    }
  }, [resolvedStreamId]);

  useEffect(() => {
    fetchApprovedComments();
  }, [fetchApprovedComments]);

  if (!resolvedStreamId) {
    return <Box sx={{ p: 2 }}>Stream ID not found.</Box>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {!hideHeaderDetails && (
        <BookingAndDeceasedDetails streamId={resolvedStreamId} />
      )}

      {/* Tab Nav */}
      <TabButtons
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        commentsWaitingApproval={commentsWaitingApproval}
      />

      {/* Active Tabs */}
      <Box component={"section"} sx={{ mt: 1 }}>
        {activeTab === "tribute-comments" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <CommentsTable
              commentsType={"tribute comments"}
              endpoint={`/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=generic`}
              fetchApprovedComments={fetchApprovedComments}
            />
          </Box>
        )}

        {activeTab === "youtube-comments" && (
          <Box
            sx={{
              backgroundColor: reduxTheme.primary_colors[900],
              p: 1,
              mt: 2,
              borderRadius: 3,
            }}
          >
            <CommentsTable
              commentsType={"youtube comments"}
              endpoint={`/api/chats/memorial-message/?stream_id=${resolvedStreamId}&comment_source=youtube`}
              fetchApprovedComments={fetchApprovedComments}
            />
          </Box>
        )}

        {activeTab === "media" && <MemoryTableGallery />}
        {activeTab === "upload" && <MemoryTableUpload />}
        {activeTab === "delivery" && (
          <MemoryBookDelivery setActiveTab={setActiveTab} />
        )}
      </Box>
    </motion.div>
  );
};

export default MemoryTableView2;
